import { Switch } from '@dnb/eufemia';
import { dereference } from '@portals/shared/common/open-api/resolver';
import { useLocalStorage } from '@portals/shared-frontend/hooks';
import type { OpenAPIV3 } from 'openapi-types';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';

import { HeroContent } from '@/components/HeroPage/HeroContent';
import HeroMenu from '@/components/HeroPage/HeroMenu';
import HeroMobileMenu from '@/components/HeroPage/HeroMobileMenu';
import { NAVBAR_HEADER_HEIGHT } from '@/constants';
import { TopOfContentWrapper } from '@/pages/api-documentation/components/TopOfContentWrapper';
import { LinkType, methods, SearchItem, TagType } from '@/pages/api-documentation/constants/types';
import ApiReferenceSearch from '@/pages/api-documentation/sections/reference/components/ApiReferenceSearch';
import { ReferenceContentView } from '@/pages/api-documentation/sections/reference/components/ReferenceView/components/ReferenceContentView';

import Menu from './components/Menu';

import { StyledMenu } from '@/pages/api-documentation/sections/reference/components/ReferenceView/index.styles';
import { StyledApiDocumentationPage } from '@/pages/api-documentation/styles/Api.Reference.styles';

// Use double request animation frame to ensure the browser has completed layout and paint
// Need double rAF to get to second frame when layout is done and avoid scrolling to wrong
// position due to layout changes after first frame
const waitUntilLayoutCompletelyRendered = () =>
  new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

interface Props {
  reference: OpenAPIV3.Document;
  dropdown?: ReactNode;
  loading?: boolean;
}

const ReferenceView = ({ reference, dropdown, loading }: Props) => {
  /**
   * A deep copy of the reference object before our parsing adds fields such as 'referenceName'
   *
   * This can be removed in the future if the 'dereference' function in shared/common/open-api/resolver.ts
   * is updated to not modify the original object.
   */
  const referenceCopy = useMemo(() => structuredClone(reference), [reference]);

  const {
    info,
    paths,
    tags: refTags,
    components,
    servers,
    security,
  } = useMemo(() => dereference(referenceCopy), [referenceCopy]);

  const [compact, setCompact] = useLocalStorage('api_reference_compact_mode', false);

  const [items, setItems] = useState<string[]>([]);
  const [menuItems, setMenuItems] = useState<Record<string, LinkType[]>>();
  const [tags, setTags] = useState<TagType[] | undefined>();
  const [search, setSearch] = useState<string[] | undefined>();
  const [searchItems, setSearchItems] = useState<SearchItem[] | undefined>();
  const [hasOperationsWithoutTag, setHasOperationsWithoutTag] = useState(false);

  const navigateTo = useCallback((hash = '', jump = false) => {
    // It is important that the url is updated without actual navigation, so that the page does not reload.
    if (globalThis.location.hash !== hash) {
      globalThis.window.history.replaceState(null, '', globalThis.location.pathname + hash);
    }
    if (jump) {
      document.getElementById(hash.replace('#', ''))?.scrollIntoView({ behavior: 'auto' });
    }
  }, []);

  useEffect(() => {
    const allItems: string[] = [];
    const allTags: TagType[] =
      (refTags &&
        refTags.map((tag) => {
          return {
            name: tag.name,
            href: tag.name.replaceAll(' ', '-'),
            description: tag?.description,
          };
        })) ||
      [];
    const allMenuItems: Record<string, LinkType[]> = {};
    const allSearchItems: SearchItem[] = [];
    let hasOperationsWithoutTag = false;

    // ADD ITEMS TO THE DISCOVERY LIST.
    allItems.push('introduction');
    if (components?.securitySchemes) {
      allItems.push('security-schemes');
      Object.keys(components.securitySchemes).forEach((schema) => {
        allItems.push(`security-schemes/${schema}`);
      });
    }

    Object.entries(paths).forEach(([pathKey, pathItem]) => {
      if (!pathItem || typeof pathItem === 'string') return;

      methods.forEach((httpMethod) => {
        const operationObject = pathItem[httpMethod];
        if (!operationObject) return;

        const operationTag = operationObject?.tags?.[0];
        if (!operationTag) {
          hasOperationsWithoutTag = true;
        }
        const operationTagHref = operationTag?.replaceAll(' ', '-');
        if (operationTag && operationTagHref && !allTags.some((tag) => tag.name === operationTag)) {
          allTags.push({
            name: operationTag,
            href: operationTagHref,
          });
        }
        const newMenuItemKey = operationTagHref ?? '';

        const keyPrefix = operationTagHref ? `tags/${newMenuItemKey}` : 'tags';
        const keySuffix = `${httpMethod}${pathKey}`.replaceAll('{', '').replaceAll('}', '');
        const key = `${keyPrefix}/operation/${keySuffix}`;

        allItems.push(keyPrefix, key);
        allSearchItems.push({
          name: keySuffix,
          description: `${operationObject.summary ?? ''} ${operationObject.description ?? ''}`.trim() || null,
        });

        const newMenuItem = {
          path: keySuffix,
          operator: httpMethod,
          description: operationObject.description ?? '',
          name: operationObject.summary ?? '',
        };

        if (allMenuItems[newMenuItemKey]) {
          allMenuItems[newMenuItemKey] = [...allMenuItems[newMenuItemKey], newMenuItem];
        } else {
          allMenuItems[newMenuItemKey] = [newMenuItem];
        }
      });
    });
    setTags(allTags);
    setSearchItems(allSearchItems);
    setMenuItems(allMenuItems);
    setItems(allItems);
    setHasOperationsWithoutTag(hasOperationsWithoutTag);
  }, [components?.securitySchemes, paths, refTags]);

  async function scrollToHash(hash: string) {
    // wait until the layout is completely rendered before trying to scroll to the element,
    //  otherwise it might not scroll to the correct position
    await waitUntilLayoutCompletelyRendered();

    const element = document.getElementById(hash.replace('#', ''));
    if (element) {
      const top = element.getBoundingClientRect().top + window.scrollY - NAVBAR_HEADER_HEIGHT;
      window.scrollTo({ top, behavior: 'auto' });
    }
  }

  useEffect(() => {
    const hash = globalThis.location.hash;
    if (!hash || !referenceCopy || !tags) return;
    scrollToHash(hash);
  }, [referenceCopy, tags]);

  return (
    <>
      <HeroMenu noPadding>
        <StyledMenu>
          <div className="flex flex-col gap-8">
            <ApiReferenceSearch items={searchItems} searchResult={setSearch} />
            {tags && (
              <Menu
                compact={compact}
                components={components}
                items={items}
                menuItems={menuItems}
                mobile={false}
                navigateTo={navigateTo}
                paths={paths}
                search={search}
                tags={tags}
              />
            )}
          </div>
        </StyledMenu>
      </HeroMenu>
      <HeroMobileMenu hidden={loading}>
        <StyledMenu>
          <div className="flex flex-col gap-8">
            <ApiReferenceSearch items={searchItems} searchResult={setSearch} />
            {tags && (
              <Menu
                compact={compact}
                components={components}
                items={items}
                menuItems={menuItems}
                mobile
                navigateTo={navigateTo}
                paths={paths}
                search={search}
                tags={tags}
              />
            )}
          </div>
        </StyledMenu>
      </HeroMobileMenu>
      <HeroContent>
        <TopOfContentWrapper>
          <Switch
            checked={compact}
            label="Compact Mode"
            labelPosition="left"
            onChange={({ checked }) => setCompact(checked)}
          />
          {dropdown}
        </TopOfContentWrapper>
        <StyledApiDocumentationPage>
          <ReferenceContentView
            compact={compact}
            components={components}
            hasOperationsWithoutTag={hasOperationsWithoutTag}
            info={info}
            paths={paths}
            security={security}
            servers={servers}
            tags={tags}
          />
        </StyledApiDocumentationPage>
      </HeroContent>
    </>
  );
};

export default ReferenceView;
